// Eskom PARCC — pure report-model builder (the testable heart of Reports).
//
// `buildReportModel` takes the (caller-scoped) transactions + findings and the
// report meta, and shapes them into a plain-data `ReportModel` describing each
// slide's CONTENT as data. It owns NO maths of its own: every figure is folded
// out of `computeDashboard` (src/domain/dashboard.ts) and the dashboard derive
// helpers (src/features/dashboard/derive.ts), so the report numbers are by
// construction the same as the dashboard's. The PPTX / PDF glue then lays this
// model onto slides / pages — the model is deterministic (the timestamp + date
// strings are passed IN, never read from the clock) and free of pptxgenjs / DOM,
// so it can be unit-tested as plain data.
import type { Transaction } from '../../data/types';
import { computeDashboard, type DashboardKpis } from '../../domain/dashboard';
import { canonicalDivision } from '../../domain/divisions';
import {
  anchorBreakdown,
  themeBreakdown,
  datasetCoverage,
  divisionStats,
  breachBreakdown,
  trendByPeriod,
  type AnchorBucket,
  type ThemeBucket,
  type DatasetCoverage,
  type DivisionStat,
  type BreachBucket,
  type TrendPoint,
} from '../../features/dashboard/derive';
import { genuineFindings, summariseFindings, findingOpen, type FindingSummary } from '../../domain/findings';
import { buildSelfDeclaredRefs } from '../../domain/gaps';
import type { FindingRead } from '../../lib/db';
import { formatRand } from '../../lib/money';
import type { RfiRow as RfiRegisterRow, RtmSignoffRow } from '../../data/types';
import { DISTRIBUTION_LEVELS, levelColour } from '../dashboard/palette';
import { normalizeDivisionFacet } from '../../domain/normalizeFacets';
import {
  datasetSummaryRows,
  divisionSummaryRows,
  quarterSummaryRows,
  divisionReviewUpdates,
  divisionGapBreakdownRows,
  gapsSelfDeclarationNarrative,
  gapTierSplit,
  withLevels,
  type SummaryRow,
  type DivisionReviewUpdate,
  type GapBreakdownRow,
  type TierSplit,
} from './deckSections';

/** Report scoping + presentation meta — all passed in, none derived from the clock. */
export interface ReportMeta {
  /** Human report date, e.g. "01 June 2026" (formatted by the caller). */
  dateLong: string;
  /** FY / period title, e.g. "FY26 Q1 - Q4". */
  periodTitle: string;
  /** Scope label shown on the cover, e.g. "Q1 - Q4 / All periods" or a division. */
  scopeLabel: string;
  /** The active division filter ('' = all divisions). */
  divisionFilter: string;
  /** ISO timestamp the report was generated (for determinism + audit). */
  generatedAtIso: string;
}

/** One compliance-distribution bar: level, count, % of population, Rand value, colour. */
export interface DistributionRow {
  level: string;
  count: number;
  /** Percentage of the in-scope population (0–100, rounded). */
  pct: number;
  value: number;
  /** Brand colour for the bar (dashboard palette). */
  colour: string;
}

/** The eight executive-summary KPI tiles, as label / value-string pairs. */
export interface ReportKpiTile {
  label: string;
  value: string;
  /** Mark outstanding / exposure tiles so the glue can colour them "bad". */
  tone: 'neutral' | 'good' | 'warn' | 'bad';
}

/** Per-division self-declaration roll-up (count of self-declared transactions). */
export interface SelfDeclarationRow {
  division: string;
  records: number;
  selfDeclared: number;
}

/** Per-division RFI summary, derived from the imported RFI register. */
export interface RfiSummaryRow {
  division: string;
  total: number;
  withConcerns: number;
}

/** The full report described as plain data — one section per slide group. */
export interface ReportModel {
  meta: ReportMeta;
  /** The raw dashboard KPIs (reused verbatim from computeDashboard). */
  kpis: DashboardKpis;
  /** Genuine distinct findings in scope, split by disposition (identified =
   *  outstanding + self-declared + resolved). */
  findings: FindingSummary;
  /** Executive-summary tiles (formatted strings + tone). */
  kpiTiles: ReportKpiTile[];
  /** Compliance distribution bars (present levels only, canonical order). */
  distribution: DistributionRow[];
  /** Per-dataset coverage (datasetCoverage). */
  bySource: DatasetCoverage[];
  /** Per-division risk profile (divisionStats). */
  byDivision: DivisionStat[];
  /** Regulatory-anchor breakdown (anchorBreakdown). */
  anchors: AnchorBucket[];
  /** Breach counts by category — policy / PFMA / legislative (item 1). */
  breaches: BreachBucket[];
  /** Recurring-compliance trend over the audit period (item 1). */
  trend: TrendPoint[];
  /** Recurring control-theme breakdown (themeBreakdown). */
  themes: ThemeBucket[];
  /** Auto-generated key-highlights narrative lines. */
  highlights: string[];
  /** Auto-generated key-challenges / risks narrative lines. */
  challenges: string[];
  /** Per-division self-declaration roll-up. */
  selfDeclaration: SelfDeclarationRow[];
  /** The imported RFI register rows (the report's RFI register pages). */
  rfiRegister: RfiRegisterRow[];
  /** Per-division RFI summary (counts derived from the register). */
  rfiSummary: RfiSummaryRow[];
  /** Per-operating-unit RTM sign-off status. */
  rtm: RtmSignoffRow[];
  /** Deck FY summary by dataset (population/reviewed/gaps/compliant/self-declared/outstanding/IE). */
  datasetSummary: SummaryRow[];
  /** Deck divisional summary by operating unit. */
  divisionSummary: SummaryRow[];
  /** Deck per-quarter summary. */
  quarterSummary: SummaryRow[];
  /** Gap counts split by PFMA / Policy & Procedures / Housekeeping tier. */
  gapTiers: TierSplit[];
  /** R3 #4: per-division Review Update blocks (client deck slides 13–18). */
  divisionUpdates: DivisionReviewUpdate[];
  /**
   * R7 (27 Jul 2026): the row model behind the two new client slides — the FY27
   * divisional summary and the gaps/self-declaration summary. One builder feeds both,
   * so the two slides can never disagree about the same division.
   */
  divisionGapBreakdown: GapBreakdownRow[];
  /** Narrative line above the gaps/self-declaration table, derived from its Total row. */
  gapsSelfDeclarationNarrative: string;
}

/** Prefix marking a self-declared compliance level (any "Self-Declared:" level). */
const SELF_DECLARED_PREFIX = 'Self-Declared:';

/** Build the executive-summary KPI tiles from the dashboard + findings figures. */
function buildKpiTiles(kpis: DashboardKpis, findings: FindingSummary): ReportKpiTile[] {
  return [
    { label: 'Total population', value: kpis.totalTransactions.toLocaleString('en-ZA'), tone: 'neutral' },
    { label: 'Reviewed', value: kpis.reviewed.toLocaleString('en-ZA'), tone: 'neutral' },
    { label: 'Compliant', value: kpis.compliant.toLocaleString('en-ZA'), tone: 'good' },
    // Findings = distinct non-compliant criteria still OUTSTANDING — the closure model
    // reworked from the 30 Jul hide (Lindiwe R9): a declared or resolved finding no
    // longer counts as outstanding, so the opening slide no longer reads as "nothing
    // resolved". Self-declared sits beside it so the addressed work is on the same
    // slide; identified = outstanding + self-declared + resolved.
    { label: 'Findings', value: findings.outstanding.toLocaleString('en-ZA'), tone: 'warn' },
    { label: 'Self-declared', value: findings.selfDeclared.toLocaleString('en-ZA'), tone: 'warn' },
    { label: 'Possible IE', value: kpis.possibleIeCount.toLocaleString('en-ZA'), tone: 'bad' },
    { label: 'Value in Possible IE', value: formatRand(kpis.possibleIeValue) || 'R 0', tone: 'bad' },
  ];
}

/** Compliance distribution: present levels only, in canonical enum order. */
function buildDistribution(kpis: DashboardKpis): DistributionRow[] {
  const total = kpis.totalTransactions;
  return DISTRIBUTION_LEVELS.filter((level) => (kpis.byLevel[level]?.count ?? 0) > 0).map(
    (level) => {
      const bucket = kpis.byLevel[level] ?? { count: 0, value: 0 };
      return {
        level,
        count: bucket.count,
        pct: total > 0 ? Math.round((bucket.count / total) * 100) : 0,
        value: bucket.value,
        colour: levelColour(level),
      };
    },
  );
}

/** Auto-generated executive highlights (mirrors the monolith's highlights slide). */
function buildHighlights(kpis: DashboardKpis): string[] {
  const reviewPct =
    kpis.totalTransactions > 0
      ? Math.round((kpis.reviewed / kpis.totalTransactions) * 100)
      : 0;
  return [
    `${kpis.reviewed.toLocaleString('en-ZA')} reviews completed from a population of ${kpis.totalTransactions.toLocaleString('en-ZA')}.`,
    `${reviewPct}% review completion across the in-scope population.`,
    `${kpis.compliant.toLocaleString('en-ZA')} transactions fully resolved / compliant.`,
    `${kpis.recordsWithFindings.toLocaleString('en-ZA')} transactions carry exception lines (${kpis.recoveryCoveragePct}% coverage).`,
    `${kpis.possibleIeCount.toLocaleString('en-ZA')} transactions classified as Possible IE.`,
    `Potential IE exposure in scope: ${formatRand(kpis.possibleIeValue) || 'R 0'}.`,
  ];
}

/** Auto-generated key-challenges / risks (mirrors the monolith's challenges slide). */
function buildChallenges(kpis: DashboardKpis, anchors: AnchorBucket[]): string[] {
  const pending =
    (kpis.byLevel['Pending Review']?.count ?? 0) + (kpis.byLevel['Not Reviewed']?.count ?? 0);
  const anchorMix = anchors
    .filter((a) => a.lines > 0)
    .map((a) => `${a.anchor} ${a.lines.toLocaleString('en-ZA')}`)
    .join(', ');
  return [
    `${pending.toLocaleString('en-ZA')} transactions remain pending review and may delay completion.`,
    // Report the possible-IE exposure (the meaningful risk figure), split the way
    // the client's own deck reports it, instead of the value of ALL flagged
    // transactions (which mixed IE with policy + housekeeping) — owner feedback Aug 2026.
    `Rand value – Potential IE based on Unresolved Gaps: ${formatRand(kpis.possibleIeValueUnresolved) || 'R 0'}.`,
    `Rand value – Potential IE to be Self Declared: ${formatRand(kpis.possibleIeValueSelfDeclared) || 'R 0'}.`,
    `Pending review value still to be cleared: ${formatRand(kpis.pendingReviewValue) || 'R 0'}.`,
    anchorMix
      ? `Outstanding findings by anchor: ${anchorMix}.`
      : 'No regulatory-anchor findings recorded in scope.',
    'Self-declaration items should be reconciled against signed forms and CIR numbers where applicable.',
  ];
}

/**
 * Per-division self-declaration roll-up, sorted by division name.
 *
 * Canonicalised (R7 fix): this read RAW `t.division` while every other division
 * roll-up folds aliases, so live data — which carries both "GX- Head Office" and
 * "Gx", and two different misspellings of ICT — split this slide into rows no other
 * slide splits.
 */
function buildSelfDeclaration(rows: readonly Transaction[]): SelfDeclarationRow[] {
  const byDivision = new Map<string, { records: number; selfDeclared: number }>();
  for (const t of rows) {
    const division = normalizeDivisionFacet(t.division) || t.division || 'Unspecified';
    const bucket = byDivision.get(division) ?? { records: 0, selfDeclared: 0 };
    bucket.records += 1;
    if ((t.complianceLevel ?? '').startsWith(SELF_DECLARED_PREFIX)) bucket.selfDeclared += 1;
    byDivision.set(division, bucket);
  }
  return [...byDivision.entries()]
    .map(([division, b]) => ({ division, records: b.records, selfDeclared: b.selfDeclared }))
    .sort((a, b) => a.division.localeCompare(b.division));
}

/** Per-division RFI summary (counts + concern counts) from the imported register. */
function buildRfiSummary(rfi: readonly RfiRegisterRow[]): RfiSummaryRow[] {
  const byDivision = new Map<string, { total: number; withConcerns: number }>();
  for (const r of rfi) {
    // Canonical division so ICT / Tactical / Gx variants land in one row, not two
    // (owner feedback Aug 2026: the RFI-by-division slide showed duplicates).
    const division = canonicalDivision(r.division) || 'Unspecified';
    const bucket = byDivision.get(division) ?? { total: 0, withConcerns: 0 };
    bucket.total += 1;
    const c = (r.concerns ?? '').trim();
    if (c && !/^(none|n\/a|-)$/i.test(c)) bucket.withConcerns += 1;
    byDivision.set(division, bucket);
  }
  return [...byDivision.entries()]
    .map(([division, b]) => ({ division, total: b.total, withConcerns: b.withConcerns }))
    .sort((a, b) => a.division.localeCompare(b.division));
}

/**
 * Shape the (already scoped + non-deleted) transactions + findings into a
 * `ReportModel`. The caller filters the population (by division / period) and
 * formats the meta; this function only reuses computeDashboard + the derive
 * helpers and shapes their output into the report's sections. Deterministic:
 * no clock reads, no DOM.
 */
export function buildReportModel(
  transactions: readonly Transaction[],
  findings: readonly FindingRead[],
  meta: ReportMeta,
  rfi: readonly RfiRegisterRow[] = [],
  rtm: readonly RtmSignoffRow[] = [],
  /**
   * `referenceNo → effective compliance level` (findings + override rolled up). When
   * supplied, the KPI figures read it instead of the stored `complianceLevel`, so the
   * report reconciles with the dashboard + register. The caller builds it from the FULL
   * non-deleted finding set ({@link effectiveComplianceByRef}) — not the reportable subset.
   */
  effectiveLevelByRef?: ReadonlyMap<string, string>,
): ReportModel {
  const rows = transactions.filter((t) => !t.deleted);
  // Transactions the reviewer self-declared — every finding on one is cleared by that
  // declaration (Lindiwe, 16 Aug 2026). From the stored level / gap status, no cycle.
  const sdRefs = buildSelfDeclaredRefs(rows);
  // The findings we IDENTIFIED: genuine, distinct non-compliant criteria (one shared
  // definition with the dashboard + register) — additional-comments notes and
  // duplicate (transaction × criterion) rows excluded.
  const lines = genuineFindings(findings);
  // Still OUTSTANDING (not on a self-declared transaction, and not self-declared or
  // resolved on its own) — the basis for the current-risk analytics (breaches, anchors,
  // trend, themes), so a declared breach is not reported as an outstanding one.
  const outstandingLines = lines.filter((f) => findingOpen(f, sdRefs.has(f.referenceNo ?? '')));
  // Refs carrying a genuine finding (INCLUDING resolved ones) — so the summary tables
  // count a buyer fix (a transaction now Compliant but with a finding on record) as an
  // identified-and-addressed gap, not a forgotten one (RTM Workstream I, Sep 2026).
  const findingRefs = new Set(lines.map((f) => f.referenceNo).filter((r): r is string => r != null));

  const kpis = computeDashboard(rows, effectiveLevelByRef);
  // The summary tables classify on the same effective level as the KPI tiles
  // (R5 #2 reconciliation); blank effective entries fall back to the stored
  // level. Rebased ONCE here so the five deck builders share one clone.
  const basedRows = withLevels(
    rows,
    effectiveLevelByRef
      ? (t: Transaction) => effectiveLevelByRef.get(t.referenceNo) ?? t.complianceLevel ?? ''
      : undefined,
  );
  // Join findings to their transaction at the EFFECTIVE level so a gap counts
  // "closed" once the transaction is remediated (self-declared / resolved), exactly
  // as the register rolls it up. Same map feeds the trend time-axis (the tx's year).
  const txByRef = new Map(basedRows.filter((t) => t.referenceNo).map((t) => [t.referenceNo, t]));
  const findingSummary = summariseFindings(findings, sdRefs);
  const anchors = anchorBreakdown(outstandingLines);
  // Built once: the narrative line is derived from this table's Total row, so they
  // cannot state different figures.
  const divisionGapBreakdown = divisionGapBreakdownRows(basedRows);

  return {
    meta,
    kpis,
    findings: findingSummary,
    kpiTiles: buildKpiTiles(kpis, findingSummary),
    distribution: buildDistribution(kpis),
    bySource: datasetCoverage(rows, lines),
    byDivision: divisionStats(rows),
    anchors,
    breaches: breachBreakdown(lines, sdRefs),
    trend: trendByPeriod(outstandingLines, txByRef),
    themes: themeBreakdown(outstandingLines),
    highlights: buildHighlights(kpis),
    challenges: buildChallenges(kpis, anchors),
    selfDeclaration: buildSelfDeclaration(rows),
    rfiRegister: [...rfi],
    rfiSummary: buildRfiSummary(rfi),
    rtm: [...rtm],
    datasetSummary: datasetSummaryRows(basedRows, undefined, findingRefs),
    divisionSummary: divisionSummaryRows(basedRows, undefined, findingRefs),
    quarterSummary: quarterSummaryRows(basedRows, undefined, findingRefs),
    gapTiers: gapTierSplit(basedRows),
    divisionUpdates: divisionReviewUpdates(basedRows),
    divisionGapBreakdown,
    gapsSelfDeclarationNarrative: gapsSelfDeclarationNarrative(divisionGapBreakdown),
  };
}
